// src/components/landing/PlannerBlock.tsx
// Bloco 03 — O Assistente GRAIA | GRAIA Landing
// Prefixo CSS: plnb-

import React, { useState, useEffect, useRef } from 'react';
import { Calendar, RefreshCw, Layers, Clock, Target, BookOpen, CheckCircle } from 'lucide-react';
import './PlannerBlock.css';

type DemoPhase = 'idle' | 'part1' | 'part2' | 'part3';
type ActiveModal = 'session' | 'absence' | null;

// ─────────────────────────────────────────────
// DADOS ESTÁTICOS
// ─────────────────────────────────────────────
const WEEK_DAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const SCHEDULE_ITEMS = [
    { day: 0, label: 'Anatomia', time: '08:00', type: 'aula' },
    { day: 0, label: 'Academia', time: '18:00', type: 'pessoal' },
    { day: 1, label: 'Fisiologia', time: '10:00', type: 'aula' },
    { day: 1, label: 'Flashcards', time: '20:00', type: 'revisao' },
    { day: 2, label: 'Plantão', time: '07:00', type: 'plantao' },
    { day: 2, label: 'Corrida', time: '06:00', type: 'pessoal' },
    { day: 3, label: 'Bioquímica', time: '09:00', type: 'aula' },
    { day: 3, label: 'Estudo', time: '14:00', type: 'estudo' },
    { day: 4, label: 'Revisão', time: '14:00', type: 'revisao' },
    { day: 4, label: 'Academia', time: '19:00', type: 'pessoal' },
    { day: 5, label: 'Simulado', time: '09:00', type: 'estudo' },
    { day: 5, label: 'Descanso', time: '15:00', type: 'pessoal' },
];

const REVIEW_ITEMS = [
    { id: 1, subject: 'Sistema Renina-Angiotensina', status: 'atrasado', next: 'Atrasado 2 dias' },
    { id: 2, subject: 'Ciclo de Krebs', status: 'proximo', next: 'Próximo em 1 dia' },
    { id: 3, subject: 'Anatomia do Coração', status: 'proximo', next: 'Próximo em 3 dias' },
    { id: 4, subject: 'Farmacocinética', status: 'distante', next: 'Próximo em 12 dias' },
    { id: 5, subject: 'Imunologia Básica', status: 'distante', next: 'Próximo em 18 dias' },
];

const EXPLANATION_CARDS = [
    {
        icon: Layers,
        title: 'Sistema Integrado',
        text: 'Rotina acadêmica, compromissos pessoais e metas de estudo coexistem no mesmo fluxo.',
    },
    {
        icon: RefreshCw,
        title: 'Revisão Programada',
        text: 'As revisões são distribuídas automaticamente. Cada execução ajusta o ciclo seguinte com base no seu desempenho.',
    },
    {
        icon: Target,
        title: 'Decisão com Contexto',
        text: 'As prioridades de estudo emergem dos seus dados — não da sua memória ou improviso.',
    },
];

// ─────────────────────────────────────────────
// DEMO PART 1 — FOCO
// Modal gerenciado pelo pai via onModalChange
// ─────────────────────────────────────────────
const DemoPart1: React.FC<{
    active: boolean;
    onModalChange: (m: ActiveModal) => void;
}> = ({ active, onModalChange }) => {
    const [step, setStep] = useState<'idle' | 'modal' | 'running'>('idle');
    const [timer, setTimer] = useState(0);
    const [metaProgress, setMetaProgress] = useState(62);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        if (!active) {
            setStep('idle'); setTimer(0); setMetaProgress(62);
            onModalChange(null);
            return;
        }
        const t1 = setTimeout(() => { setStep('modal'); onModalChange('session'); }, 400);
        const t2 = setTimeout(() => { setStep('running'); onModalChange(null); }, 1800);
        const t3 = setTimeout(() => setMetaProgress(63), 2400);
        return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    useEffect(() => {
        if (step === 'running') {
            timerRef.current = setInterval(() => setTimer(p => p + 1), 1000);
        } else {
            if (timerRef.current) clearInterval(timerRef.current);
        }
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, [step]);

    const fmt = (s: number) =>
        `00:${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

    return (
        <div className="plnb-demo-part">
            <div className="plnb-mock-header">
                <span className="plnb-mock-logo">GRAIA</span>
                <span className="plnb-mock-sep" />
                <span className="plnb-mock-subtitle">Painel de Estudo</span>
            </div>

            <div className="plnb-metrics-row">
                <div className="plnb-metric">
                    <span className="plnb-metric-val plnb-metric-val--mono">{fmt(timer)}</span>
                    <span className="plnb-metric-label">Tempo de foco</span>
                </div>
                <div className="plnb-metric">
                    <span className="plnb-metric-val">{metaProgress}%</span>
                    <span className="plnb-metric-label">Meta semanal</span>
                </div>
                <div className="plnb-metric">
                    <span className="plnb-metric-val">14</span>
                    <span className="plnb-metric-label">Cards revisados</span>
                </div>
            </div>

            <div className="plnb-meta-bar-wrap">
                <div className="plnb-meta-bar-track">
                    <div className="plnb-meta-bar-fill" style={{ width: `${metaProgress}%` }} />
                </div>
            </div>

            {step !== 'running' ? (
                <button className={`plnb-btn-start ${step === 'modal' ? 'plnb-btn-start--loading' : ''}`}>
                    {step === 'modal' ? 'Iniciando...' : 'Iniciar Sessão'}
                </button>
            ) : (
                <div className="plnb-session-active">
                    <span className="plnb-session-dot" />
                    <span className="plnb-session-label">Sessão ativa · Fisiologia · Flashcards</span>
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────
// DEMO PART 2 — ROTINA
// ─────────────────────────────────────────────
const DemoPart2: React.FC<{
    active: boolean;
    onModalChange: (m: ActiveModal) => void;
}> = ({ active, onModalChange }) => {
    const [showBadge, setShowBadge] = useState(false);

    useEffect(() => {
        if (!active) { setShowBadge(false); onModalChange(null); return; }
        const t1 = setTimeout(() => onModalChange('absence'), 500);
        const t2 = setTimeout(() => { onModalChange(null); setShowBadge(true); }, 2000);
        return () => { clearTimeout(t1); clearTimeout(t2); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    return (
        <div className="plnb-demo-part">
            <div className="plnb-mock-header">
                <span className="plnb-mock-logo">GRAIA</span>
                <span className="plnb-mock-sep" />
                <span className="plnb-mock-subtitle">Agenda Semanal</span>
            </div>

            <div className="plnb-week-grid">
                {WEEK_DAYS.map((day, i) => (
                    <div key={day} className="plnb-week-col">
                        <span className="plnb-week-day">{day}</span>
                        {SCHEDULE_ITEMS.filter(item => item.day === i).map((item, j) => (
                            <div
                                key={j}
                                className={`plnb-week-item plnb-week-item--${item.type} ${i === 0 && j === 0 ? 'plnb-week-item--clicked' : ''}`}
                            >
                                <span className="plnb-week-item-time">{item.time}</span>
                                <span className="plnb-week-item-label">{item.label}</span>
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            {showBadge && (
                <div className="plnb-absence-badge">
                    <CheckCircle size={12} />
                    Revisão sugerida adicionada.
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────
// DEMO PART 3 — CONTINUIDADE
// ─────────────────────────────────────────────
const DemoPart3: React.FC<{ active: boolean }> = ({ active }) => {
    const [completedId, setCompletedId] = useState<number | null>(null);
    const [autoAppear, setAutoAppear] = useState(false);

    useEffect(() => {
        if (!active) { setCompletedId(null); setAutoAppear(false); return; }
        const t1 = setTimeout(() => setCompletedId(1), 700);
        const t2 = setTimeout(() => setAutoAppear(true), 1600);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, [active]);

    return (
        <div className="plnb-demo-part">
            <div className="plnb-mock-header">
                <span className="plnb-mock-logo">GRAIA</span>
                <span className="plnb-mock-sep" />
                <span className="plnb-mock-subtitle">Revisões Pendentes</span>
            </div>

            <div className="plnb-review-list">
                {REVIEW_ITEMS.map(item => {
                    const isDone = completedId === item.id;
                    return (
                        <div
                            key={item.id}
                            className={`plnb-review-row ${isDone ? 'plnb-review-row--done' : `plnb-review-row--${item.status}`}`}
                        >
                            <div className={`plnb-review-dot plnb-review-dot--${isDone ? 'done' : item.status}`} />
                            <div className="plnb-review-info">
                                <span className="plnb-review-subject">{item.subject}</span>
                                <span className="plnb-review-next">
                                    {isDone ? 'Reagendado para 9 dias.' : item.next}
                                </span>
                            </div>
                            {!isDone && item.id === 1 && (
                                <button className="plnb-review-check" onClick={() => setCompletedId(1)}>
                                    Feito
                                </button>
                            )}
                        </div>
                    );
                })}

                {autoAppear && (
                    <div className="plnb-review-row plnb-review-row--auto">
                        <div className="plnb-review-dot plnb-review-dot--auto" />
                        <div className="plnb-review-info">
                            <span className="plnb-review-subject">Potencial de Ação</span>
                            <span className="plnb-review-next plnb-review-next--auto">
                                ↻ Reinserido automaticamente · Próximo em 4 dias
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────
const PlannerBlock: React.FC = () => {
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);
    const [activePhase, setActivePhase] = useState<DemoPhase>('idle');
    const [activeTab, setActiveTab] = useState<0 | 1 | 2>(0);
    // Estado do modal elevado ao shell — garante inset:0 sobre todo o shell
    const [activeModal, setActiveModal] = useState<ActiveModal>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) { setVisible(true); observer.disconnect(); }
            },
            { threshold: 0.12 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!visible) return;
        const t1 = setTimeout(() => { setActivePhase('part1'); setActiveTab(0); }, 300);
        const t3 = setTimeout(() => { setActivePhase('part2'); setActiveTab(1); }, 5000);
        const t4 = setTimeout(() => { setActivePhase('part3'); setActiveTab(2); }, 9500);
        return () => [t1, t3, t4].forEach(clearTimeout);
    }, [visible]);

    const TABS = [
        { label: 'Foco', icon: Clock },
        { label: 'Rotina', icon: Calendar },
        { label: 'Continuidade', icon: BookOpen },
    ];

    return (
        <section
            className={`plnb-section ${visible ? 'plnb-section--visible' : ''}`}
            ref={sectionRef}
        >
            <div className="plnb-container">

                {/* ── CABEÇALHO ── */}
                <div className="plnb-header">
                    <p className="plnb-label">Assistente de Planejamento</p>
                    <h2 className="plnb-headline">
                        Aprender exige constância.<br />Constância exige sistema.
                    </h2>
                    <p className="plnb-subheadline">
                        O Assistente GRAIA integra rotina, metas, revisões e desempenho em um único sistema contínuo — que trabalha enquanto você estuda.
                    </p>
                </div>

                {/* ── GRID PRINCIPAL ── */}
                <div className="plnb-grid">

                    {/* ─── COLUNA ESQUERDA: DEMO ─── */}
                    <div className="plnb-demo-col">
                        <div className="plnb-tabs">
                            {TABS.map((tab, i) => {
                                const Icon = tab.icon;
                                return (
                                    <button
                                        key={tab.label}
                                        className={`plnb-tab ${activeTab === i ? 'plnb-tab--active' : ''}`}
                                        onClick={() => {
                                            setActiveTab(i as 0 | 1 | 2);
                                            setActivePhase(['part1', 'part2', 'part3'][i] as DemoPhase);
                                            setActiveModal(null);
                                        }}
                                    >
                                        <Icon size={13} strokeWidth={1.75} />
                                        {tab.label}
                                    </button>
                                );
                            })}
                        </div>

                        {/*
                            plnb-mock-shell tem position:relative + overflow:hidden.
                            Os slides ficam dentro dele normalmente.
                            O modal overlay é IRMÃO dos slides — não filho de nenhum slide.
                            Assim position:absolute + inset:0 cobre o shell INTEIRO,
                            independente de qual slide está ativo ou da sua altura.
                            Resultado: zero área branca descoberta.
                        */}
                        <div className="plnb-mock-shell">

                            <div className={`plnb-demo-slide ${activeTab === 0 ? 'plnb-demo-slide--active' : ''}`}>
                                <DemoPart1
                                    active={activePhase === 'part1'}
                                    onModalChange={setActiveModal}
                                />
                            </div>
                            <div className={`plnb-demo-slide ${activeTab === 1 ? 'plnb-demo-slide--active' : ''}`}>
                                <DemoPart2
                                    active={activePhase === 'part2'}
                                    onModalChange={setActiveModal}
                                />
                            </div>
                            <div className={`plnb-demo-slide ${activeTab === 2 ? 'plnb-demo-slide--active' : ''}`}>
                                <DemoPart3 active={activePhase === 'part3'} />
                            </div>

                            {/* Modal irmão dos slides — cobre 100% do shell */}
                            {activeModal === 'session' && (
                                <div className="plnb-modal-overlay" key="session">
                                    <div className="plnb-modal-inner" style={{ width: 210 }}>
                                        <p className="plnb-modal-title">Iniciar Sessão</p>
                                        <div className="plnb-modal-field">
                                            <span className="plnb-modal-field-label">Tipo</span>
                                            <span className="plnb-modal-field-val">Flashcards</span>
                                        </div>
                                        <div className="plnb-modal-field">
                                            <span className="plnb-modal-field-label">Disciplina</span>
                                            <span className="plnb-modal-field-val">Fisiologia</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeModal === 'absence' && (
                                <div className="plnb-modal-overlay" key="absence">
                                    <div className="plnb-modal-inner" style={{ width: 190 }}>
                                        <p className="plnb-modal-title">Anatomia · 08:00</p>
                                        <p className="plnb-modal-sub">Segunda-feira</p>
                                        <button className="plnb-absence-btn">Marcar ausência</button>
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>

                    {/* ─── COLUNA DIREITA: CARDS ─── */}
                    <div className="plnb-cards-col">
                        {EXPLANATION_CARDS.map((card) => {
                            const Icon = card.icon;
                            return (
                                <div key={card.title} className="plnb-card">
                                    <div className="plnb-card-icon">
                                        <Icon size={16} strokeWidth={1.75} />
                                    </div>
                                    <div className="plnb-card-body">
                                        <h3 className="plnb-card-title">{card.title}</h3>
                                        <p className="plnb-card-text">{card.text}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                </div>

                {/* ── DIFERENCIAÇÃO ── */}
                <div className="plnb-diff">
                    <p className="plnb-diff-title">Por que não é um aplicativo de tarefas</p>
                    <ul className="plnb-diff-list">
                        <li>Não organiza apenas horários</li>
                        <li>Transforma faltas em reposição estruturada</li>
                        <li>Redistribui conteúdo ao longo do tempo</li>
                        <li>Conecta rotina e aprendizagem em um único ciclo</li>
                    </ul>
                </div>

                {/* ── FECHAMENTO ── */}
                <div className="plnb-closing">
                    <p className="plnb-closing-main">
                        Você estuda.<br />
                        <strong>Ele organiza.</strong>
                    </p>
                </div>

            </div>
        </section>
    );
};

export default PlannerBlock;